import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class ProductBomService {
    constructor(private datatask: DataTaskService) {}

    /**
     * @datatask 1129
     * @datataskName sp_ws_p2_w_productbom_get
     * @param params the product id to get the bom for.
     * @returns promise containing the bom.
     */
    public async getProductBom(params: GetProductBomParams): Promise<ProductBomGet> {
        return (await this.datatask.Post(1129, params))[0];
    }

    /**
     * @datatask 1130
     * @datataskName sp_ws_p2_w_productbom_list
     * @param params the product to get boms for.
     * @returns promise containing list of the product structure.
     */
    public async getProductBomList(params: GetProductBomListParams): Promise<ProductBomItem[]> {
        return this.datatask.Post(1130, params);
    }

    /**
     * @datatask 1135
     * @datataskName sp_ws_p2_w_productbom_prod_id_selected_get
     * @param params the selected product id for the bom.
     * @returns promise containing data about the selected product.
     */
    public async getSelectedProduct(params: ProductBomSelectedProductParams): Promise<ProductBomSelectedProduct> {
        return (await this.datatask.Post(1135, params))[0];
    }

    /**
     * @datatask 2314
     * @datataskName sp_ws_p2_w_productbom_errorcheck
     * @param params product to error check.
     * @returns validation for the error check.
     */
    public async errorCheck(params: ProductBomErrorCheckParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2314, params))[0];
    }

    /**
     * @datatask 3180
     * @datataskName sp_ws_p2_w_productbom_update_bom
     * @param params the product to set verified status on and the verification status.
     * @returns promise when the call has resolved.
     */
    public async updateBom(params: UpdateProductBomParams): Promise<void> {
        return await this.datatask.Post(3180, params);
    }

    /**
     * @datatask 2930
     * @datataskName sp_ws_p2_w_productbom_own_list
     * @param params for the kpi to load.
     * @returns promise containing list of kpi data.
     */
    public async getOwnKPIList(params: ProductBomGet & ProductBomSelectedProduct): Promise<ProductBomOwnKPIItem[]> {
        return await this.datatask.Post(2930, params);
    }

    /**
     * @datatask 1137
     * @datatask sp_ws_p2_w_productbom_includedin_list
     * @param params the prod_id of the product to find included in other products.
     * @returns promise containing list over products the product is used in.
     */
    public getProductsProductIsUsedIn(params: GetProductsProductIsUsedInParams): Promise<ProductBomProductsUsedInItem[]> {
        return this.datatask.Post(1137, params);
    }
}

export interface ProductBomProductsUsedInItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_parms: string;
    item_path: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    orderby: string;
    item_filtervalue: string;
}

export interface UpdateProductBomParams {
    prod_id: string;
    bom_verified: '1' | '0';
}

export interface GetProductsProductIsUsedInParams {
    prod_id: string;
    productversion_keyno?: string;
}

export interface ProductBomErrorCheckParams {
    prod_id: string;
}

export interface GetProductBomListParams {
    prod_id: string;
    productversion_keyno?: string;
}

export interface GetProductBomParams {
    prod_id: string;
}

export interface ProductBomGet {
    prod_id: string;
    prod_id_selected: string;
    prod_name_selected: string;
    webpage_heading: string;
    prod_keyno_selected: string;
    nbr_of_components_selected: string;
    url_picture: string;
    gb_productbom_list: string;
    gb_productbom_other: string;
    show_setting: '1' | '0';
    show_button_errorcheck: '1' | '0';
    show_dataharvest: '1' | '0';
    show_button_move_choice: '1' | '0';
    setting_show_assemblygroup_select_list: '1' | '0';
    setting_use_productversion: '1' | '0';
}

export interface ProductBomSelectedProductParams {
    prod_id: string;
    prod_id_selected: string;
}

export interface ProductBomSelectedProduct {
    prod_name_selected: string;
    prod_keyno_selected: string;
    url_picture: string;
    product_main_image: string;
    assemblygroup_no: string;
    productversion_keyno: string;
}

export interface ProductBomItem {
    node_id: string;
    parent_node_id: string;
    item_id: string;
    item_keyno: string;
    item_name: string;
    item_id_parent: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    orderby: string;
    isexpanded: '1' | '0';
    bom_verified: '1' | '0';
    comp_keyno: string;
    comp_keyno_belongto: string;
}

export interface ProductBomOwnKPIItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_parms: string;
    item_path: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    orderby: string;
}
